import { useCallback, useEffect, useRef, useState } from 'react'
import {
  api,
  ApiError,
  type Customer,
  type LookupItem,
  type Order,
  type Warehouse,
} from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, ConfirmDialog, Field, Input, Notice, Select } from '../ui/controls'

/**
 * Касса: заказ из нескольких позиций.
 *
 * Отличается от быстрого списания (п. 4.14) наличием черновика. Кассир
 * набирает позиции, при необходимости называет покупателя, и только
 * подтверждение списывает товар со склада. Пока заказ черновик, склад
 * не тронут: резервирования нет, и остаток уменьшается ровно в момент
 * подтверждения.
 *
 * Работа идёт с клавиатуры: поиск, Enter — позиция добавлена, фокус
 * вернулся в поиск. За смену кассир повторяет это сотни раз, и каждое
 * лишнее движение мышью — потерянное время.
 */
const WAREHOUSE_KEY = 'checkout.warehouse'

export default function CheckoutPage() {
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.stock_ops

  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [warehouseId, setWarehouseId] = useState(0)
  const [order, setOrder] = useState<Order | null>(null)
  const [recent, setRecent] = useState<Order[]>([])
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [pending, setPending] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)

  // Поиск позиции
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<LookupItem[]>([])
  const [quantity, setQuantity] = useState('1')
  const searchRef = useRef<HTMLInputElement>(null)

  // Покупатель
  const [customerQuery, setCustomerQuery] = useState('')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [newCustomer, setNewCustomer] = useState<{ name: string; phone: string } | null>(null)

  const loadRecent = useCallback(async () => {
    setRecent(await api.orders())
  }, [])

  useEffect(() => {
    void api.warehouses().then((list) => {
      setWarehouses(list)
      const saved = Number(localStorage.getItem(WAREHOUSE_KEY))
      const pick = list.find((w) => w.id === saved) ?? list[0]
      if (pick) setWarehouseId(pick.id)
    })
    void loadRecent()
  }, [loadRecent])

  useEffect(() => {
    if (warehouseId) localStorage.setItem(WAREHOUSE_KEY, String(warehouseId))
  }, [warehouseId])

  // Поиск позиции по мере ввода. Остаток берётся на складе заказа,
  // чтобы кассир видел доступное, а не общее по компании.
  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setFound([])
      return
    }
    const timer = setTimeout(() => {
      void api
        .stockLookup(q, order?.warehouse_id ?? warehouseId)
        .then(setFound)
        .catch(() => setFound([]))
    }, 200)
    return () => clearTimeout(timer)
  }, [query, warehouseId, order?.warehouse_id])

  useEffect(() => {
    const q = customerQuery.trim()
    const timer = setTimeout(() => {
      void api.customers(q).then(setCustomers).catch(() => setCustomers([]))
    }, 200)
    return () => clearTimeout(timer)
  }, [customerQuery])

  async function run<T>(fn: () => Promise<T>, onDone?: (v: T) => void) {
    setPending(true)
    setNotice(null)
    try {
      const v = await fn()
      onDone?.(v)
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось' })
    } finally {
      setPending(false)
    }
  }

  async function startOrder() {
    await run(
      () => api.createOrder({ warehouse_id: warehouseId }),
      (o) => {
        setOrder(o)
        void loadRecent()
        searchRef.current?.focus()
      },
    )
  }

  async function addLine(item: LookupItem) {
    if (!order) return
    const n = Number(quantity) || 1
    const already = order.lines.find((l) => l.product_id === item.product_id)?.quantity ?? 0
    await run(
      () => api.setOrderLine(order.id, item.product_id, already + n),
      (o) => {
        setOrder(o)
        setQuery('')
        setFound([])
        setQuantity('1')
        searchRef.current?.focus()
      },
    )
  }

  const busy = pending || !canWrite
  // Заказ маркетплейса ведётся иначе: позиции приходят извне, а каждое
  // действие сообщается площадке. Кассовые кнопки к нему не годятся.
  const external = order?.source === 'external'

  return (
    <section className="max-w-6xl">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Касса</h2>
          <p className="mt-0.5 text-sm text-ink-500">
            Заказ из нескольких позиций. Товар списывается при подтверждении —
            пока заказ черновик, склад не затронут.
          </p>
        </div>
        {!order && (
          <div className="field-row grid grid-cols-[minmax(0,12rem)_auto]">
            <Field label="Склад отпуска">
              <Select value={warehouseId} onChange={(e) => setWarehouseId(Number(e.target.value))}
              >
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="field-action"><Button className="field-action" variant="primary" onClick={startOrder} disabled={busy || warehouseId === 0}>
              Новый заказ
            </Button></div>
          </div>
        )}
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {order && (
        <div className="mb-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
          <div className="space-y-4">
            {/* Набор позиций */}
            <Block
              title={`Заказ ${order.number}`}
              aside={
                <span className="text-xs text-ink-500">
                  {order.warehouse_name} · <StateTag state={order.state} />
                  {order.channel_name && (
                    <>
                      {' · '}
                      {order.channel_name} №{order.external_id}
                      {order.external_status && ` · ${externalStatusTitle(order.external_status)}`}
                    </>
                  )}
                </span>
              }
            >
              {order.state === 'draft' && (
                <div className="mb-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_6rem]">
                  <Field label="Позиция" hint="Артикул, название или штрихкод">
                    <Input
                      ref={searchRef}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => {
                        // Единственное совпадение добавляется по Enter,
                        // не отрывая рук от клавиатуры (А-1302).
                        if (e.key === 'Enter' && found.length > 0) {
                          e.preventDefault()
                          void addLine(found[0])
                        }
                      }}
                      placeholder="начните вводить…"
                      disabled={busy}
                      autoFocus
                    />
                  </Field>
                  <Field label="Кол-во">
                    <Input
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ''))}
                      disabled={busy}
                      className="text-right tabular-nums"
                    />
                  </Field>
                </div>
              )}

              {found.length > 0 && order.state === 'draft' && (
                <ul className="mb-4 divide-y divide-ink-100 rounded-md border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
                  {found.slice(0, 6).map((f) => (
                    <li key={f.product_id}>
                      <button
                        type="button"
                        onClick={() => void addLine(f)}
                        disabled={busy}
                        className="flex w-full items-baseline justify-between gap-4 px-3 py-2 text-left text-sm hover:bg-ink-50 dark:hover:bg-ink-900"
                      >
                        <span className="min-w-0">
                          <span className="font-mono text-xs text-ink-500">{f.sku}</span>{' '}
                          <span className="text-ink-900 dark:text-ink-100">{f.name}</span>
                        </span>
                        <span className="shrink-0 tabular-nums text-ink-500">
                          {f.price.toLocaleString('ru')} ₸ · остаток {f.stock}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {order.lines.length === 0 ? (
                <p className="text-sm text-ink-500">
                  Позиций пока нет. Найдите товар в поле выше и нажмите Enter.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-ink-500">
                    <tr className="border-b border-ink-200 dark:border-ink-800">
                      <th className="py-2 pr-4 text-left font-medium">Товар</th>
                      <th className="py-2 pr-4 text-right font-medium">Цена</th>
                      <th className="py-2 pr-4 text-right font-medium">Кол-во</th>
                      <th className="py-2 pr-4 text-right font-medium">Сумма</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {order.lines.map((l) => (
                      <tr key={l.product_id} className="border-b border-ink-100 dark:border-ink-900">
                        <td className="py-1.5 pr-4">
                          <div className="text-ink-900 dark:text-ink-100">{l.name}</div>
                          <div className="font-mono text-xs text-ink-500">
                            {l.product_id === 0 ? (
                              <span className="text-danger-700 dark:text-danger-100">
                                код {l.external_code} — товар не сопоставлен
                              </span>
                            ) : (
                              l.sku
                            )}
                            {l.quantity > l.available && (
                              <span className="ml-2 text-danger-700 dark:text-danger-100">
                                на складе только {l.available}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">
                          {l.price.toLocaleString('ru')}
                        </td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">{l.quantity}</td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">
                          {l.sum.toLocaleString('ru')}
                        </td>
                        <td className="py-1.5 text-right">
                          {order.state === 'draft' && (
                            <button
                              type="button"
                              onClick={() =>
                                void run(
                                  () => api.setOrderLine(order.id, l.product_id, 0),
                                  setOrder,
                                )
                              }
                              disabled={busy}
                              aria-label={`Убрать ${l.name}`}
                              className="px-1 text-ink-400 hover:text-danger-700 dark:hover:text-danger-100"
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Block>
          </div>

          {/* Итог и покупатель */}
          <div className="space-y-4">
            <Block title="Итог">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-ink-500">
                  {order.positions} позиций · {order.units} шт
                </span>
                <span className="text-2xl font-semibold tabular-nums text-ink-900 dark:text-ink-50">
                  {order.total.toLocaleString('ru')} ₸
                </span>
              </div>

              {external ? (
                <MarketplaceActions
                  order={order}
                  busy={busy}
                  onDone={(o, text) => {
                    setOrder(o)
                    void loadRecent()
                    if (text) setNotice({ kind: 'success', text })
                  }}
                  onError={(text) => setNotice({ kind: 'error', text })}
                  setPending={setPending}
                />
              ) : order.state === 'draft' ? (
                <div className="mt-4 space-y-2">
                  <Button
                    variant="primary"
                    onClick={() =>
                      void run(() => api.confirmOrder(order.id), (o) => {
                        setOrder(o)
                        void loadRecent()
                        setNotice({
                          kind: 'success',
                          text: `Заказ ${o.number} подтверждён, товар списан`,
                        })
                      })
                    }
                    disabled={busy || order.lines.length === 0}
                    className="w-full"
                  >
                    Подтвердить и списать
                  </Button>
                  <button
                    type="button"
                    onClick={() =>
                      void run(() => api.deleteOrder(order.id), () => {
                        setOrder(null)
                        void loadRecent()
                      })
                    }
                    disabled={busy}
                    className="w-full py-1 text-sm text-ink-500 hover:text-danger-700 dark:hover:text-danger-100"
                  >
                    Удалить черновик
                  </button>
                </div>
              ) : (
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-ink-500">
                    {order.state === 'confirmed'
                      ? `Списан ${order.confirmed_at?.slice(0, 16).replace('T', ' ')}`
                      : `Отменён ${order.cancelled_at?.slice(0, 16).replace('T', ' ')}`}
                  </p>
                  {order.state === 'confirmed' && (
                    <Button
                      variant="danger"
                      onClick={() => setConfirmCancel(true)}
                      disabled={busy}
                      className="w-full"
                    >
                      Отменить заказ
                    </Button>
                  )}
                  <Button onClick={() => setOrder(null)} className="w-full">
                    Закрыть
                  </Button>
                </div>
              )}
            </Block>

            <Block title="Покупатель">
              {order.customer_name ? (
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-ink-900 dark:text-ink-100">{order.customer_name}</span>
                  {order.state === 'draft' && (
                    <button
                      type="button"
                      onClick={() => void run(() => api.setOrderCustomer(order.id, null), setOrder)}
                      disabled={busy}
                      className="text-xs text-ink-500 hover:text-danger-700 dark:hover:text-danger-100"
                    >
                      Убрать
                    </button>
                  )}
                </div>
              ) : order.state !== 'draft' ? (
                <p className="text-sm text-ink-500">Без покупателя</p>
              ) : newCustomer ? (
                <div className="space-y-3">
                  <Field label="Имя">
                    <Input
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                      autoFocus
                    />
                  </Field>
                  <Field label="Телефон" hint="Необязательно">
                    <Input
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    />
                  </Field>
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      onClick={() =>
                        void run(
                          async () => {
                            const c = await api.createCustomer({
                              name: newCustomer.name,
                              phone: newCustomer.phone || null,
                            })
                            return api.setOrderCustomer(order.id, c.id)
                          },
                          (o) => {
                            setOrder(o)
                            setNewCustomer(null)
                          },
                        )
                      }
                      disabled={busy || newCustomer.name.trim() === ''}
                    >
                      Сохранить
                    </Button>
                    <button
                      type="button"
                      onClick={() => setNewCustomer(null)}
                      className="text-sm text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <Input
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="имя или телефон"
                    disabled={busy}
                  />
                  {customers.length > 0 && (
                    <ul className="mt-2 max-h-48 divide-y divide-ink-100 overflow-y-auto rounded-md border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
                      {customers.slice(0, 8).map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() =>
                              void run(() => api.setOrderCustomer(order.id, c.id), setOrder)
                            }
                            disabled={busy}
                            className="flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-ink-50 dark:hover:bg-ink-900"
                          >
                            <span className="text-ink-900 dark:text-ink-100">{c.name}</span>
                            <span className="shrink-0 text-xs text-ink-500">{c.phone ?? ''}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    type="button"
                    onClick={() => setNewCustomer({ name: customerQuery, phone: '' })}
                    disabled={busy}
                    className="mt-2 text-sm text-brand-600 hover:underline dark:text-brand-300"
                  >
                    + Новый покупатель
                  </button>
                </>
              )}
            </Block>
          </div>
        </div>
      )}

      {/* Журнал заказов */}
      <Block title="Заказы">
        {recent.length === 0 ? (
          <p className="text-sm text-ink-500">Заказов ещё не было.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-ink-500">
              <tr className="border-b border-ink-200 dark:border-ink-800">
                <th className="py-2 pr-4 text-left font-medium">Номер</th>
                <th className="py-2 pr-4 text-left font-medium">Когда</th>
                <th className="py-2 pr-4 text-left font-medium">Покупатель</th>
                <th className="py-2 pr-4 text-left font-medium">Склад</th>
                <th className="py-2 pr-4 text-right font-medium">Позиций</th>
                <th className="py-2 pr-4 text-right font-medium">Сумма</th>
                <th className="py-2 text-left font-medium">Состояние</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => void run(() => api.order(o.id), setOrder)}
                  className="cursor-pointer border-b border-ink-100 hover:bg-ink-50 dark:border-ink-900 dark:hover:bg-ink-900"
                >
                  <td className="py-1.5 pr-4 font-mono text-xs">{o.number}</td>
                  <td className="py-1.5 pr-4 whitespace-nowrap">
                    {o.created_at.slice(0, 16).replace('T', ' ')}
                  </td>
                  <td className="max-w-[12rem] truncate py-1.5 pr-4">{o.customer_name ?? '—'}</td>
                  <td className="py-1.5 pr-4">{o.warehouse_name}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{o.positions}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">
                    {o.total.toLocaleString('ru')}
                  </td>
                  <td className="py-1.5">
                    <StateTag state={o.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>

      {confirmCancel && order && (
        <ConfirmDialog
          title={`Отменить заказ ${order.number}?`}
          message="Списание будет компенсировано движением с причиной «возврат». Исходное движение останется в журнале."
          confirmLabel="Отменить заказ"
          pending={pending}
          onCancel={() => setConfirmCancel(false)}
          onConfirm={() => {
            setConfirmCancel(false)
            void run(() => api.cancelOrder(order.id), (o) => {
              setOrder(o)
              void loadRecent()
              setNotice({ kind: 'warning', text: `Заказ ${o.number} отменён, товар возвращён` })
            })
          }}
        />
      )}
    </section>
  )
}

function StateTag({ state }: { state: Order['state'] }) {
  const map: Record<string, { text: string; cls: string }> = {
    draft: { text: 'черновик', cls: 'text-ink-500' },
    confirmed: { text: 'списан', cls: 'text-success-700 dark:text-success-100' },
    cancelled: { text: 'отменён', cls: 'text-ink-400 line-through' },
  }
  const v = map[state] ?? { text: state, cls: '' }
  return <span className={`text-xs ${v.cls}`}>{v.text}</span>
}

function Block({
  title,
  aside,
  children,
}: {
  title: string
  aside?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="mb-6 rounded-lg border border-ink-200 bg-white p-4 dark:border-ink-800 dark:bg-ink-950">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
        {aside}
      </div>
      {children}
    </div>
  )
}

/**
 * Действия над заказом маркетплейса.
 *
 * Каждое из них меняет и наш склад, и состояние в кабинете площадки,
 * поэтому подписи говорят о последствии, а не о переходе: «Принять
 * и списать» вместо «В работу».
 */
function MarketplaceActions({
  order,
  busy,
  onDone,
  onError,
  setPending,
}: {
  order: Order
  busy: boolean
  onDone: (o: Order, text?: string) => void
  onError: (text: string) => void
  setPending: (v: boolean) => void
}) {
  const [places, setPlaces] = useState('1')
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('MERCHANT_OUT_OF_STOCK')
  const [comment, setComment] = useState('')

  const unmatched = order.lines.some((l) => l.product_id === 0)
  const status = order.external_status ?? ''

  async function act(fn: () => Promise<Order>, text: string) {
    setPending(true)
    try {
      onDone(await fn(), text)
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Не удалось')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="mt-4 space-y-3">
      {unmatched && (
        <p className="text-sm text-warning-700 dark:text-warning-100">
          Не все позиции связаны с товарами каталога. Свяжите их в разделе
          «Товары», иначе списывать нечего.
        </p>
      )}

      {status === 'APPROVED_BY_BANK' && (
        <Button
          variant="primary"
          className="w-full"
          disabled={busy || unmatched}
          onClick={() => void act(() => api.acceptOrder(order.id), 'Заказ принят, товар списан')}
        >
          Принять и списать
        </Button>
      )}

      {status === 'ACCEPTED_BY_MERCHANT' && (
        <div className="flex items-end gap-2">
          <Field label="Мест">
            <Input
              value={places}
              onChange={(e) => setPlaces(e.target.value.replace(/\D/g, ''))}
              className="w-16 text-right tabular-nums"
            />
          </Field>
          <Button
            variant="primary"
            className="flex-1"
            disabled={busy}
            onClick={() =>
              void act(
                () => api.assembleOrder(order.id, Number(places) || 1),
                'Заказ собран, накладная запрошена',
              )
            }
          >
            Собрать
          </Button>
        </div>
      )}

      {order.waybill_number && (
        <div className="rounded-md border border-ink-200 px-3 py-2 text-sm dark:border-ink-800">
          <div className="text-xs uppercase text-ink-500">Накладная</div>
          <div className="mt-0.5 font-mono">{order.waybill_number}</div>
          {order.waybill_url && (
            <a
              href={order.waybill_url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-xs text-brand-600 hover:underline dark:text-brand-300"
            >
              Печать ярлыка →
            </a>
          )}
        </div>
      )}

      {status === 'ASSEMBLE' && (
        <Button
          variant="primary"
          className="w-full"
          disabled={busy}
          onClick={() => void act(() => api.completeOrder(order.id), 'Заказ выдан покупателю')}
        >
          Выдан покупателю
        </Button>
      )}

      {(status === 'APPROVED_BY_BANK' || status === 'ACCEPTED_BY_MERCHANT') &&
        (rejecting ? (
          <div className="space-y-2 rounded-md border border-ink-200 p-3 dark:border-ink-800">
            <Field label="Причина отмены">
              <Select value={reason} onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-sm text-ink-900 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
              >
                <option value="MERCHANT_OUT_OF_STOCK">Товара нет в наличии</option>
                <option value="BUYER_NOT_REACHABLE">Покупатель недоступен</option>
                <option value="BUYER_CANCELLATION_BY_MERCHANT">Покупатель отказался</option>
              </Select>
            </Field>
            <Field label="Комментарий" hint="Необязательно">
              <Input value={comment} onChange={(e) => setComment(e.target.value)} />
            </Field>
            <div className="flex gap-2">
              <Button
                variant="danger"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => api.rejectOrder(order.id, reason, comment || undefined),
                    'Заказ отменён, товар возвращён',
                  )
                }
              >
                Отменить заказ
              </Button>
              <button
                type="button"
                onClick={() => setRejecting(false)}
                className="text-sm text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
              >
                Не отменять
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setRejecting(true)}
            disabled={busy}
            className="w-full py-1 text-sm text-ink-500 hover:text-danger-700 dark:hover:text-danger-100"
          >
            Отменить заказ
          </button>
        ))}

      {order.delivery_address && (
        <p className="text-xs text-ink-500">Доставка: {order.delivery_address}</p>
      )}
    </div>
  )
}

/** Состояние заказа у площадки по-русски. */
function externalStatusTitle(s: string): string {
  const map: Record<string, string> = {
    APPROVED_BY_BANK: 'новый',
    ACCEPTED_BY_MERCHANT: 'принят',
    ASSEMBLE: 'собран',
    COMPLETED: 'выдан',
    CANCELLED: 'отменён',
    CANCELLING: 'отменяется',
    RETURNED: 'возвращён',
  }
  return map[s] ?? s
}
